import { ChangeDetectionStrategy, Component } from '@angular/core';

import { TabBarComponent } from './tab-bar.component';
import { money } from './money';
import { currentLocale } from './locale';

/**
 * Today: "What can I spend today?"
 *
 * Every string here is translated, because the app is offered in the same five
 * languages this site is. A drawn screen is a picture of the app, so a German
 * page showing an English phone would be showing the wrong app.
 *
 * Structure taken from a 0.3.0 screenshot:
 *
 * - Three rows under the hairline, not two. The app subtracts what is still
 *   expected before the period ends, then divides by the days that are left.
 * - The week chart is wide bars over a labelled axis with a rule under it.
 *   Today's bar is the accent; the rest are neutral. There is no track behind
 *   an unfilled day, just nothing.
 * - A floating add button clears the tab bar at 96px.
 *
 * The arithmetic is real and it is the spine of the whole page:
 *
 *     1,652 flexible − 500 expected = 1,152, over 18 days = 64 a day
 *
 * That 1,152 is the same figure the Plan screen shows as left to spend, and
 * the 64 is the one the hero puts at the top of the page. Change one of these
 * numbers and you have to change all three.
 */
@Component({
  selector: 'app-screen-today',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TabBarComponent],
  template: `
    <div class="nq-screen">
      <div class="band">
        <div class="kicker-row">
          <span class="kicker">{{ t.date }}</span>
          <span class="pill">
            {{ t.budgetPill }} <span class="pill__caret">⌄</span>
          </span>
        </div>

        <div class="hero hero--today">
          <p class="hero__label">{{ t.safeLabel }}</p>
          <p class="hero__figure hero__figure--today">{{ safeToSpend }}</p>

          <div class="arith">
            @for (row of arithmetic; track row.key) {
            <div class="arith__row">
              <span class="arith__key">{{ row.key }}</span>
              <span class="arith__value">{{ row.value }}</span>
            </div>
            }
          </div>
        </div>
      </div>

      <div class="ground">
        <div class="week">
          <div class="week__bars">
            @for (day of week; track $index) {
            <span class="week__col">
              <span
                class="week__bar"
                [class.week__bar--today]="day.today"
                [style.height.%]="day.fill"
              ></span>
            </span>
            }
          </div>
          <div class="week__axis">
            @for (day of week; track $index) {
            <span class="week__label" [class.week__label--today]="day.today">{{
              day.today ? t.axisToday : day.label
            }}</span>
            }
          </div>
        </div>

        <p class="section">{{ t.sectionToday }}</p>
        @for (tx of today; track tx.name) {
        <div class="row">
          <span class="mark" [style.background]="tx.hue">{{ tx.initial }}</span>
          <span class="row__body">
            <span class="row__title">{{ tx.name }}</span>
            <span class="row__sub">{{ tx.sub }}</span>
          </span>
          <span class="row__amount">{{ tx.amount }}</span>
        </div>
        }

        <div class="divider"></div>

        <p class="section">{{ t.sectionUpcoming }}</p>
        @for (tx of upcoming; track tx.name) {
        <div class="row row--expected">
          <span class="mark" [style.background]="tx.hue">{{ tx.initial }}</span>
          <span class="row__body">
            <span class="row__title">{{ tx.name }}</span>
            <span class="row__sub">{{ tx.sub }}</span>
          </span>
          <span class="row__amount">{{ tx.amount }}</span>
        </div>
        }

        <span class="link">{{ t.allActivity }} →</span>
      </div>

      <span class="fab" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M12 5v14M5 12h14" />
        </svg>
      </span>

      <app-tab-bar active="today" />
    </div>
  `,
  styles: [
    `
      /* Today's kicker to hero gap is 28px. Every other screen is 26. */
      .nq-screen .hero--today {
        padding-top: 28px;
      }

      /* The largest figure in the app. */
      .nq-screen .hero__figure--today {
        margin-top: 11.2px;
        font-size: 46px;
        line-height: 1.04;
      }

      /* This hairline is --color-section-ghost, not --color-line. */
      .nq-screen .arith {
        margin-top: 16.8px;
        border-top: 1px solid var(--color-section-ghost);
      }

      .nq-screen .arith__row {
        display: flex;
        justify-content: space-between;
        padding-top: 8.4px;
        font-size: 12px;
        font-weight: 400;
      }

      .nq-screen .arith__key {
        color: var(--color-accent-200);
      }

      .nq-screen .arith__value {
        color: var(--color-neutral-100);
      }

      .nq-screen .week {
        padding-bottom: 8.4px;
        border-bottom: 1px solid var(--color-line);
      }

      .nq-screen .week__bars {
        display: flex;
        align-items: flex-end;
        gap: 5.6px;
        height: 64px;
      }

      .nq-screen .week__col {
        display: flex;
        align-items: flex-end;
        justify-content: center;
        flex: 1;
        height: 100%;
      }

      /* No track behind an empty day: an unspent day draws nothing. */
      .nq-screen .week__bar {
        display: block;
        width: 100%;
        min-height: 4px;
        border-radius: 2px;
        background: var(--color-neutral-400);
      }

      .nq-screen .week__bar--today {
        background: var(--color-accent);
      }

      .nq-screen .week__axis {
        display: flex;
        gap: 5.6px;
        margin-top: 8.4px;
      }

      .nq-screen .week__label {
        flex: 1;
        text-align: center;
        font-size: 10px;
        font-weight: 400;
        letter-spacing: 0.08em;
        color: var(--color-neutral-500);
      }

      .nq-screen .week__label--today {
        color: var(--color-accent);
      }
    `,
  ],
})
export class ScreenTodayComponent {
  private readonly locale = currentLocale();

  protected readonly t = {
    date: $localize`:@@frameTodayDate:Tuesday, 4 August`,
    budgetPill: $localize`:@@frameBudgetPill:My Budget`,
    safeLabel: $localize`:@@frameSafeLabel:Safe to spend today`,
    axisToday: $localize`:@@frameAxisToday:TODAY`,
    sectionToday: $localize`:@@frameSectionToday:Today`,
    sectionUpcoming: $localize`:@@frameSectionUpcoming:Upcoming`,
    allActivity: $localize`:@@frameAllActivity:All activity`,
  };

  /** (1,652 − 500) ÷ 18. The three rows under the hairline say so. */
  protected readonly safeToSpend = money(64, this.locale);

  protected readonly arithmetic = [
    {
      key: $localize`:@@frameFlexible:Flexible money left`,
      value: money(1652, this.locale),
    },
    {
      key: $localize`:@@frameExpected:Expected before 31 August`,
      value: money(-500, this.locale),
    },
    {
      key: $localize`:@@frameDaysRemaining:Days remaining`,
      value: '18',
    },
  ];

  /**
   * The axis runs Wednesday to Tuesday. One comma separated string per locale
   * rather than seven, because the initials only make sense as a set: German
   * needs M D M D F S S where English needs W T F S S M T.
   */
  private readonly letters =
    $localize`:@@frameWeekLetters:W,T,F,S,S,M,T`.split(',');

  protected readonly week = [34, 58, 22, 71, 44, 12, 28].map((fill, i) => ({
    label: this.letters[i]?.trim() ?? '',
    fill,
    today: i === 6,
  }));

  protected readonly today = [
    {
      name: $localize`:@@frameTxGroceries:Groceries`,
      sub: $localize`:@@frameTxGroceriesSub:Corner shop · Checking`,
      amount: money(-85, this.locale),
      initial: 'F',
      hue: '#ed8a5c',
    },
    {
      name: $localize`:@@frameTxTransport:Transport`,
      sub: $localize`:@@frameTxTransportSub:Bus fare · Cash`,
      amount: money(-12, this.locale),
      initial: 'T',
      hue: '#a6c25e',
    },
  ];

  /** Totals the 500 the band expects before the period ends. */
  protected readonly upcoming = [
    {
      name: $localize`:@@frameTxInternet:Internet`,
      sub: $localize`:@@frameTxInternetSub:Repeats monthly · 18 August`,
      amount: money(-500, this.locale),
      initial: 'H',
      hue: '#6e9bf0',
    },
  ];
}
